import { Transaction, TransactionStatus } from '../store/useFinanceStore';

const COUNTABLE_STATUSES: TransactionStatus[] = ['confirmed', 'paid', 'scheduled', 'overdue'];

export const normalizeStatus = (
  transaction: Transaction,
  referenceDate: Date = new Date()
): TransactionStatus => {
  let status = transaction.status ?? 'active';

  if (status === 'active') {
    status = 'confirmed';
  }

  if (status === 'scheduled') {
    const txDate = new Date(transaction.date);
    if (!Number.isNaN(txDate.getTime()) && txDate < referenceDate) {
      status = 'overdue';
    }
  }

  return status;
};

export const isCountableTransaction = (
  transaction: Transaction,
  referenceDate: Date = new Date()
): boolean => {
  const status = normalizeStatus(transaction, referenceDate);
  return COUNTABLE_STATUSES.includes(status);
};

export const calculateMonthlyTotals = (
  transactions: Transaction[],
  referenceDate: Date = new Date()
): { income: number; expenses: number; balance: number } => {
  const month = referenceDate.getMonth();
  const year = referenceDate.getFullYear();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);

  return transactions.reduce(
    (acc, transaction) => {
      const txDate = new Date(transaction.date);
      if (Number.isNaN(txDate.getTime())) {
        return acc;
      }

      if (txDate < start || txDate > end) {
        return acc;
      }

      if (!isCountableTransaction(transaction, referenceDate)) {
        return acc;
      }

      if (transaction.amount >= 0) {
        acc.income += transaction.amount;
      } else {
        acc.expenses += Math.abs(transaction.amount);
      }

      acc.balance = acc.income - acc.expenses;
      return acc;
    },
    { income: 0, expenses: 0, balance: 0 }
  );
};

export const sumTransactions = (
  transactions: Transaction[],
  predicate: (transaction: Transaction) => boolean
): number => transactions.reduce((sum, transaction) => (predicate(transaction) ? sum + transaction.amount : sum), 0);
